// Build the per-configuration "Bill of Materials" rows + per-config rooms list
// + inclusions/exclusions for the submittal PDF. Pure functions; no React.

import { ORDERING_CATALOG, specForUnitModel } from "../data/orderingCodes";
import {
  buildBaseCode, buildAccessoryCode, buildFullCode,
} from "./orderingCodeBuilder";
import { buildUnitSchedule } from "./unitSchedule";
import { withVariantTags } from "./variantTags";

function specOf(modelId) {
  return ORDERING_CATALOG.find((m) => m.id === modelId);
}

// Models that have a single physical size — hide length in BOM / schedules.
function isLengthAgnostic(unitModel) {
  return unitModel === "Model N" || unitModel === "Model N-f";
}
// Nominal SI mapping for unit lengths (catalogue convention: 1 ft = 300 mm).
function fmtLenLabel(length_ft, isSI) {
  if (length_ft == null) return null;
  return isSI ? `${Math.round(parseFloat(length_ft) * 300)} mm` : `${length_ft} ft`;
}
function lengthCellLabel(row, isSI = false) {
  if (isLengthAgnostic(row.unit_model)) return "—";
  return fmtLenLabel(row.length_ft, isSI) || "—";
}
function detailLengthSuffix(row, isSI = false) {
  if (isLengthAgnostic(row.unit_model)) return "";
  const lbl = fmtLenLabel(row.length_ft, isSI);
  return lbl ? ` · ${lbl}` : "";
}

function fieldSelectedLabel(field, value) {
  if (field.type === "merv") return `MERV ${value}`;
  const opt = field.options?.find((o) => String(o.value) === String(value));
  return opt?.label ?? String(value ?? "");
}

function isFieldOn(field, value) {
  if (field.type === "merv") return true;
  if (field.type === "ral") return value !== 0;
  const opt = field.options?.find((o) => String(o.value) === String(value));
  if (!opt) return false;
  const lbl = (opt.label || "").trim();
  if (/^no\b/i.test(lbl)) return false;
  if (value === 0 && /^(no|nothing|none)/i.test(lbl)) return false;
  return true;
}

function buildIncExc(spec, cfg) {
  const inclusions = [];
  const exclusions = [];
  spec.baseFields.forEach((f) => {
    if (typeof f.visibleWhen === "function" && !f.visibleWhen(cfg)) return;
    // Auto-populated fields (nozzle_type, num_nozzles) are informational —
    // they aren't a customer choice, so don't surface them as inclusions.
    if (f.auto) return;
    const v = cfg.base?.[f.key];
    if (v === undefined) return;
    if (v !== f.standard) inclusions.push(`${f.label}: ${fieldSelectedLabel(f, v)}`);
  });
  spec.accessoryFields.forEach((f) => {
    if (typeof f.visibleWhen === "function" && !f.visibleWhen(cfg)) return;
    const v = cfg.accessory?.[f.key];
    const on = isFieldOn(f, v);
    if (on) {
      // Resolve the effective install location: prefer the engineer's
      // per-accessory override, fall back to the spec's `note`.
      const defaultInstall =
        f.note === "Factory Installed" ? "factory"
          : f.note === "Separate in a box" ? "box"
          : null;
      const install = cfg.accessory_install?.[f.key] || defaultInstall;
      const tag = install === "factory" ? "[FAC]"
                : install === "box" ? "[BOX]" : "";
      inclusions.push(`${tag ? tag + " " : ""}${f.label}: ${fieldSelectedLabel(f, v)}`);
    } else if (f.note) {
      exclusions.push(f.label);
    }
  });
  return { inclusions, exclusions };
}

const variantTotal = (cfg) =>
  (cfg.assignments || []).reduce((s, a) => s + (parseInt(a.qty, 10) || 0), 0);

// Schema v3: prefer lookup by room_id; fall back to room_idx for v2 payloads.
function getRoomName(rooms, roomIdx, roomId) {
  if (roomId != null) {
    const byId = (rooms || []).find((r) => r.id === roomId);
    if (byId) return byId.name || `Room ${(rooms || []).indexOf(byId) + 1}`;
  }
  return rooms?.[roomIdx]?.name || `Room ${(roomIdx ?? 0) + 1}`;
}

// Rows for the BOM table — one per saved variant.
export function buildBomRows({ rooms, results, configurations }) {
  const groups = buildUnitSchedule(rooms || [], results || []);
  const cfgs = withVariantTags(configurations || [], groups);
  const groupByKey = new Map();
  groups.forEach((g) => {
    const specId = specForUnitModel(g.unit_model)?.id || g.unit_model;
    // Dual-key every group for backward compat with saved cfg.key strings.
    // v20.7-: "specId__length"            (2 parts, no circuits)
    // v20.8:  "specId__length__circuits"  (3 parts)
    // v20.9+: "unitModel__specId__length__circuits" (4 parts — current; fixes C/C1 collision)
    // New key is set last so it wins for any group whose legacy key is ambiguous
    // (e.g. Model C and Model C1 both produce "C__4__1" in the v20.8 format).
    groupByKey.set(`${specId}__${g.length_ft ?? ""}`, g);
    groupByKey.set(`${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`, g);
    groupByKey.set(`${g.unit_model}__${specId}__${g.length_ft ?? ""}__${g.circuits ?? ""}`, g);
  });

  return cfgs.map((cfg) => {
    const g = groupByKey.get(cfg.key);
    const spec = specOf(cfg.model_id);
    // unit_model resolution: prefer the room's actual unit_model from the
    // group (so Model C1 prints "B-C1..." and Model C prints "B-C..."), fall
    // back to cfg.unit_model for cached configs that stored it directly.
    // Without this, both spec-sharing models would emit the spec.basePrefix
    // ("B-C"), losing the C/C1 distinction in the ordering code.
    const unitModel = g?.unit_model || cfg.unit_model;
    const fullCode = spec ? buildFullCode(spec, { ...cfg, unit_model: unitModel }) : "";
    const baseCode = spec ? buildBaseCode(spec, cfg.base, cfg.extras, { unitModel }) : "";
    const accCode  = spec ? buildAccessoryCode(spec, cfg.accessory, cfg.extras) : "";
    const incExc = spec ? buildIncExc(spec, cfg) : { inclusions: [], exclusions: [] };
    const qty = variantTotal(cfg);
    const roomsList = (cfg.assignments || [])
      .filter((a) => (parseInt(a.qty, 10) || 0) > 0)
      .map((a) => ({
        room_id: a.room_id || null,           // schema v3 primary key
        room_idx: a.room_idx,                 // schema v2 compat fallback
        room_name: getRoomName(rooms, a.room_idx, a.room_id),
        qty: parseInt(a.qty, 10) || 0,
      }));
    return {
      tag: cfg.tag || "—",
      variantName: cfg.name || "Default",
      unit_model: g?.unit_model || cfg.model_id || "",
      length_ft: g?.length_ft ?? null,
      qty,
      fullCode,
      baseCode,
      accCode,
      rooms: roomsList,
      inclusions: incExc.inclusions,
      exclusions: incExc.exclusions,
    };
  });
}

// Variant Summary rows — one entry per saved variant that has at least one
// unit assigned. Compact shape used by Excel/CSV/PDF summary sheets.
//   { tag, variantName, unit_model, length_ft, qty, baseCode, accCode }
export function buildVariantSummaryRows({ rooms, results, configurations }) {
  return buildBomRows({ rooms, results, configurations }).filter(
    (r) => (r.qty || 0) > 0
  );
}

// Compact unit-model label for summary tables (model + length when applicable).
function summaryModelLabel(row, isSI = false) {
  if (isLengthAgnostic(row.unit_model)) return row.unit_model;
  const lbl = fmtLenLabel(row.length_ft, isSI);
  return lbl ? `${row.unit_model} · ${lbl}` : row.unit_model;
}

// HTML for the Variant Summary page: one row per variant — Tag, Unit Model,
// Qty, Code B, Code A. Mirrors the spreadsheet summary sheet.
export function variantSummaryHTML({ rooms, results, configurations, isSI = false }) {
  const rows = buildVariantSummaryRows({ rooms, results, configurations });
  if (!rows.length) return "";

  const tbl = rows.map((r) => `
    <tr>
      <td style="padding:8px 10px;border:1px solid #e2e8f0;font-weight:700;">${r.tag}</td>
      <td style="padding:8px 10px;border:1px solid #e2e8f0;">${r.variantName}</td>
      <td style="padding:8px 10px;border:1px solid #e2e8f0;">${summaryModelLabel(r, isSI)}</td>
      <td style="padding:8px 10px;border:1px solid #e2e8f0;text-align:center;font-weight:700;">${r.qty}</td>
      <td style="padding:8px 10px;border:1px solid #e2e8f0;font-family:ui-monospace,Menlo,monospace;font-size:9px;color:#92400e;word-break:break-all;">${r.baseCode}</td>
      <td style="padding:8px 10px;border:1px solid #e2e8f0;font-family:ui-monospace,Menlo,monospace;font-size:9px;color:#047857;word-break:break-all;">${r.accCode}</td>
    </tr>`).join("");

  const totalQty = rows.reduce((s, r) => s + r.qty, 0);

  return `
    <div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:14px 0 4px;font-size:11px;">
      Variant Summary
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:10px;">
      <thead style="background:#0f172a;color:#fff;">
        <tr>
          <th style="padding:8px 10px;border:1px solid #0f172a;">Tag</th>
          <th style="padding:8px 10px;border:1px solid #0f172a;text-align:left;">Variant</th>
          <th style="padding:8px 10px;border:1px solid #0f172a;text-align:left;">Unit Model</th>
          <th style="padding:8px 10px;border:1px solid #0f172a;">QTY</th>
          <th style="padding:8px 10px;border:1px solid #0f172a;text-align:left;">Code B (Base)</th>
          <th style="padding:8px 10px;border:1px solid #0f172a;text-align:left;">Code A (Accessory)</th>
        </tr>
      </thead>
      <tbody>
        ${tbl}
        <tr style="background:#f1f5f9;font-weight:700;">
          <td colspan="3" style="padding:8px 10px;border:1px solid #e2e8f0;">Total Units</td>
          <td style="padding:8px 10px;border:1px solid #e2e8f0;text-align:center;">${totalQty}</td>
          <td colspan="2" style="padding:8px 10px;border:1px solid #e2e8f0;"></td>
        </tr>
      </tbody>
    </table>
  `;
}

// HTML for the Configuration Schedule section: BOM table + Inclusions/Exclusions.
export function configurationScheduleHTML({ rooms, results, configurations, isSI = false }) {
  const rows = buildBomRows({ rooms, results, configurations });
  // V.22: omit qty=0 rooms from BOM. Engineer's "sample" or "design-intent placeholder" rows stay in the project + Selection Report + Excel schedule but do NOT appear in Config Report or Submittal BOM.
  const visibleRows = rows.filter((r) => (r.qty ?? 0) > 0);
  if (!visibleRows.length) return "";

  const tbl = visibleRows.map((r) => {
    const lenLabel = lengthCellLabel(r, isSI);
    return `<tr>
      <td style="padding:5px;border:1px solid #e2e8f0;font-weight:600;">${r.tag}</td>
      <td style="padding:5px;border:1px solid #e2e8f0;">${r.variantName}</td>
      <td style="padding:5px;border:1px solid #e2e8f0;">${r.unit_model}</td>
      <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;">${lenLabel}</td>
      <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;font-weight:600;">${r.qty}</td>
      <td style="padding:5px;border:1px solid #e2e8f0;font-family:ui-monospace,Menlo,monospace;font-size:8px;word-break:break-all;">
        <span style="color:#92400e;">${r.baseCode}</span>
        <span style="color:#94a3b8;"> | </span>
        <span style="color:#047857;">${r.accCode}</span>
      </td>
    </tr>`;
  }).join("");

  const totalQty = visibleRows.reduce((s, r) => s + r.qty, 0);

  const detailBlocks = visibleRows.map((r) => {
    const incHTML = r.inclusions.length
      ? r.inclusions.map((x) => `<li style="margin:0;">${x}</li>`).join("")
      : `<li style="margin:0;color:#64748b;">All standard.</li>`;
    const excHTML = r.exclusions.length
      ? r.exclusions.map((x) => `<li style="margin:0;">${x}</li>`).join("")
      : `<li style="margin:0;color:#64748b;">None.</li>`;
    return `
      <div style="margin-top:10px;border:1px solid #e2e8f0;border-radius:4px;break-inside:avoid;">
        <div style="background:#0f172a;color:#fff;padding:6px 10px;font-size:10px;font-weight:600;display:flex;justify-content:space-between;">
          <span>${r.tag} · ${r.variantName} · ${r.unit_model}${detailLengthSuffix(r, isSI)} · qty ${r.qty}</span>
          <span style="font-family:ui-monospace,Menlo,monospace;font-weight:400;font-size:9px;">${r.fullCode}</span>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;">
          <div style="padding:6px 10px;border-right:1px solid #e2e8f0;font-size:9px;">
            <div style="font-weight:600;color:#047857;margin-bottom:3px;">Includes</div>
            <ul style="padding-left:14px;margin:0;line-height:1.4;">${incHTML}</ul>
          </div>
          <div style="padding:6px 10px;font-size:9px;">
            <div style="font-weight:600;color:#b91c1c;margin-bottom:3px;">Not included</div>
            <ul style="padding-left:14px;margin:0;line-height:1.4;">${excHTML}</ul>
          </div>
        </div>
      </div>
    `;
  }).join("");

  return `
    <div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:14px 0 4px;font-size:11px;">
      Configuration Schedule (Bill of Materials)
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:9px;">
      <thead style="background:#0f172a;color:#fff;">
        <tr>
          <th style="padding:5px;border:1px solid #0f172a;">Tag</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;">Variant</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;">Unit Model</th>
          <th style="padding:5px;border:1px solid #0f172a;">Length</th>
          <th style="padding:5px;border:1px solid #0f172a;">Qty</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;">Ordering Code (Base | Accessory)</th>
        </tr>
      </thead>
      <tbody>
        ${tbl}
        <tr style="background:#f1f5f9;font-weight:600;">
          <td colspan="4" style="padding:5px;border:1px solid #e2e8f0;">Total Units</td>
          <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;">${totalQty}</td>
          <td style="padding:5px;border:1px solid #e2e8f0;"></td>
        </tr>
      </tbody>
    </table>

    <div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:14px 0 4px;font-size:11px;">
      Inclusions &amp; Exclusions per Configuration
    </div>
    ${detailBlocks}
  `;
}

// HTML for the Schedule of Rooms per Ordering Code.
// One section per saved variant, listing the rooms assigned to it and the
// resulting ordering code.
export function roomsByCodeHTML({ rooms, results, configurations }) {
  const rowsBom = buildBomRows({ rooms, results, configurations });
  // Keep only variants that actually have at least one room assigned.
  const rowsWithRooms = rowsBom.filter((r) => r.rooms.length > 0);
  if (!rowsWithRooms.length) return "";

  const tableRows = [];
  rowsWithRooms.forEach((r) => {
    r.rooms.forEach((room, j) => {
      const showCodeInfo = j === 0;
      const span = r.rooms.length;
      tableRows.push(`<tr>
        ${showCodeInfo ? `<td rowspan="${span}" style="padding:5px;border:1px solid #e2e8f0;font-weight:600;vertical-align:top;">${r.tag}</td>` : ""}
        ${showCodeInfo ? `<td rowspan="${span}" style="padding:5px;border:1px solid #e2e8f0;vertical-align:top;">${r.variantName}</td>` : ""}
        <td style="padding:5px;border:1px solid #e2e8f0;">${room.room_name}</td>
        <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;font-weight:600;">${room.qty}</td>
        ${showCodeInfo ? `<td rowspan="${span}" style="padding:5px;border:1px solid #e2e8f0;font-family:ui-monospace,Menlo,monospace;font-size:8px;word-break:break-all;vertical-align:top;">
          <span style="color:#92400e;">${r.baseCode}</span>
          <span style="color:#94a3b8;"> | </span>
          <span style="color:#047857;">${r.accCode}</span>
        </td>` : ""}
      </tr>`);
    });
  });

  const totalQty = rowsWithRooms.reduce(
    (s, r) => s + r.rooms.reduce((s2, x) => s2 + x.qty, 0), 0);

  return `
    <div data-subtitle-anchor style="font-weight:600;background:#f1f5f9;padding:6px 8px;margin:14px 0 4px;font-size:11px;">
      Schedule of Rooms per Ordering Code
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:9px;">
      <thead style="background:#0f172a;color:#fff;">
        <tr>
          <th style="padding:5px;border:1px solid #0f172a;">Tag</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;">Variant</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;">Room</th>
          <th style="padding:5px;border:1px solid #0f172a;">Units</th>
          <th style="padding:5px;border:1px solid #0f172a;text-align:left;">Ordering Code (Base | Accessory)</th>
        </tr>
      </thead>
      <tbody>
        ${tableRows.join("")}
        <tr style="background:#f1f5f9;font-weight:600;">
          <td colspan="3" style="padding:5px;border:1px solid #e2e8f0;">Total Units Assigned</td>
          <td style="padding:5px;border:1px solid #e2e8f0;text-align:center;">${totalQty}</td>
          <td style="padding:5px;border:1px solid #e2e8f0;"></td>
        </tr>
      </tbody>
    </table>
  `;
}
